/* WelcomeNewUserModal — first-run celebratory welcome after registration.
   Shows once after sign-up, with a stock dog photo and CTAs to either add
   the first dog passport or jump straight into the app. */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PawPrint, ArrowRight, X, Sparkles } from "lucide-react";

const STORAGE_KEY = "orio_welcome_shown";

export default function WelcomeNewUserModal({ userName, dogName }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Show only once, only when ?welcome=1 was set by Register.jsx post-signup
    const url = new URL(window.location.href);
    const flag = url.searchParams.get("welcome");
    if (flag !== "1") return;
    if (sessionStorage.getItem(STORAGE_KEY) === "1") return;
    setOpen(true);
    sessionStorage.setItem(STORAGE_KEY, "1");
    // Cleanup the URL param so refresh doesn't re-trigger
    url.searchParams.delete("welcome");
    window.history.replaceState({}, "", url.pathname + (url.search ? "?" + url.searchParams : ""));
  }, []);

  const close = () => setOpen(false);
  const goAddDog = () => {
    setOpen(false);
    navigate("/app/cani/nuovo");
  };

  const firstName = (userName || "").split(" ")[0] || "amico";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[1500] flex items-end sm:items-center justify-center px-4 pb-6 sm:pb-4"
          data-testid="welcome-new-user-modal"
          onClick={close}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-md" />
          <motion.div
            initial={{ y: 80, scale: 0.96 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: 80, scale: 0.96 }}
            transition={{ type: "spring", damping: 26, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:max-w-md rounded-[28px] sm:rounded-3xl overflow-hidden bg-white border border-[#F0EBE3] shadow-[0_20px_60px_-10px_rgba(0,0,0,0.25)]"
          >
            {/* Stock photo header — golden retriever vibe, sfumato in basso */}
            <div className="relative h-44 sm:h-52 overflow-hidden">
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage:
                    "url(https://images.unsplash.com/photo-1591946614720-90a587da4a36?w=900&h=600&fit=crop)",
                  backgroundSize: "cover",
                  backgroundPosition: "center 40%",
                }}
              />
              {/* Warm gradient overlay for legibility + fade-down */}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(255,122,26,0.35) 0%, rgba(255,122,26,0) 50%, #FFFFFF 100%)",
                }}
              />
              <button
                onClick={close}
                aria-label="Chiudi"
                data-testid="welcome-close"
                className="absolute top-3 right-3 h-10 w-10 rounded-full bg-white/95 hover:bg-white flex items-center justify-center backdrop-blur-md shadow-md"
              >
                <X className="h-4 w-4 text-[#1A1A1A]" />
              </button>
              {/* Confetti sparkles */}
              <Sparkle x="14%" y="22%" delay={0} />
              <Sparkle x="78%" y="32%" delay={0.4} />
              <Sparkle x="44%" y="14%" delay={0.8} />
              <Sparkle x="86%" y="62%" delay={0.2} />
            </div>

            <div className="relative px-6 pt-3 pb-6 -mt-2">
              <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C25612]">
                <Sparkles className="h-3 w-3" /> Benvenut* su ORIO
              </div>
              <h2 className="mt-2 font-display text-2xl sm:text-3xl leading-tight text-[#1A1A1A]">
                Ciao {firstName}! 🎉<br />
                <span className="text-[#FF7A1A]">Ora costruiamo insieme</span> il passaporto digitale {dogName ? `di ${dogName}` : "del tuo cane"}.
              </h2>
              <p className="mt-3 text-sm text-[#6B6B6B] leading-relaxed">
                Bastano 60 secondi: foto, microchip, libretto vaccinale e un QR pubblico pronto per chiunque lo trovi.
              </p>

              <button
                onClick={goAddDog}
                data-testid="welcome-add-dog"
                className="mt-5 w-full orio-cta-btn px-6 py-3.5 rounded-full font-semibold inline-flex items-center justify-center gap-2"
              >
                <PawPrint className="h-4 w-4" />
                Crea il primo passaporto
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                onClick={close}
                data-testid="welcome-skip"
                className="mt-2 w-full px-5 py-2.5 rounded-full text-sm text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#FAF7F0] transition-colors"
              >
                Esploro prima l&apos;app
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Sparkle({ x, y, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: [0, 1, 1, 0], scale: [0.4, 1, 1, 0.6] }}
      transition={{ duration: 1.8, delay, repeat: Infinity, repeatDelay: 1.4 }}
      className="absolute"
      style={{ left: x, top: y }}
    >
      <Sparkles className="h-4 w-4 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]" />
    </motion.div>
  );
}
